import { Component, OnInit, OnDestroy } from '@angular/core';
import { ProfessionalService } from '../../services/professional.service';
import { ProfessionalStateService } from '../../services/professional-state.service';
import { ApiService } from '../../services/api.service';
import { NotificationService } from '../../services/notification.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-professionals',
  templateUrl: './professionals.component.html',
  styleUrls: ['./professionals.component.scss']
})
export class ProfessionalsComponent implements OnInit, OnDestroy {
  professionals: any[] = [];
  loading = false;
  showModal = false;
  isEditing = false;
  selectedProfessional: any = null;
  modalTab: 'dados' | 'pagamento' | 'assinatura' | 'horarios' | 'usuarios' | 'indicacoes' = 'dados';

  // Formulário profissional
  form: any = { name: '', specialty: '', professionalId: '', email: '', phone: '', notes: '',
                address: '', number: '', neighborhood: '', complement: '', city: '', state: '', zipCode: '' };

  // Formulário billing
  billing: any = {
    preferredPaymentMethod: 1,
    pixKey: '42132309000185', pixKeyType: 2, pixHolderName: 'RA DIONISIO',
    cardHolderName: '', cardLastFourDigits: '', cardBrand: '',
    cardExpiryMonth: '', cardExpiryYear: '', cardGatewayToken: '',
    isConfigured: false
  };

  // Cartão (campos temporários - nunca enviados para API diretamente)
  cardNumberDisplay = '';
  cardExpiry = '';
  cardCvv = '';

  // Assinatura e usuários carregados sob demanda
  subscription: any = null;
  users: any[] = [];

  // Aba Usuários — busca por email
  userSearchEmail = '';
  userSearchResult: any = null;
  userSearchError = '';
  userSearching = false;
  userLinkRole = 'Collaborator';
  userLinking = false;
  userRemovingId: number | null = null;

  // Convites
  invites: any[] = [];
  inviteEmail = '';
  inviteRole = 'Collaborator';
  invitePermissions = 'read';
  sendingInvite = false;

  // Tab de Indicações
  referrals: any[] = [];
  loadingReferrals = false;
  sendingReferral = false;
  deletingReferralId: number | null = null;
  referralEmail = '';
  referralFilter = { status: null as number | null };

  // Horários de atendimento
  dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
  workingHours: any[] = [];
  savingHours = false;

  // Listas de horas por período (selects 24h — cross-browser garantido)
  morningHours   = Array.from({ length: 14 }, (_, i) => i);       // 0..13
  afternoonHours = Array.from({ length: 12 }, (_, i) => i + 12);  // 12..23
  timeMinutes    = [0, 15, 30, 45, 50, 60];

  padTime(n: number): string { return String(n).padStart(2, '0'); }
  getHour(t: string): number { return parseInt((t || '00:00').split(':')[0], 10); }
  getMin (t: string): number { return parseInt((t || '00:00').split(':')[1], 10); }
  setHour(block: any, prop: string, h: number): void {
    block[prop] = `${this.padTime(h)}:${this.padTime(this.getMin(block[prop]))}`;
  }
  setMin(block: any, prop: string, m: number): void {
    block[prop] = `${this.padTime(this.getHour(block[prop]))}:${this.padTime(m)}`;
  }

  private subs = new Subscription();

  constructor(
    private svc: ProfessionalService,
    private profState: ProfessionalStateService,
    private api: ApiService,
    private notification: NotificationService
  ) {}

  ngOnInit() {
    this.load();
    // Escutar evento para abrir modal (usado pelo "Meu Cadastro")
    this.subs.add(
      this.profState.openModal$.subscribe(() => {
        const currentProf = this.profState.selected;
        if (currentProf) {
          this.open(currentProf);
        }
      })
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  load() {
    this.loading = true;
    this.svc.getAll().subscribe({
      next: d => { this.professionals = d; this.loading = false; },
      error: () => this.loading = false
    });
  }

  open(p?: any) {
    this.isEditing = !!p;
    this.selectedProfessional = p || null;
    this.modalTab = 'dados';
    this.subscription = null;
    this.users = [];

    if (p) {
      this.form = { name: p.name, specialty: p.specialty || '', professionalId: p.professionalId || '',
                    email: p.email || '', emailConfirm: p.email || '',
                    phone: p.phone ? this.formatPhone(p.phone) : '', notes: p.notes || '',
                    address: p.address || '', number: p.number || '', neighborhood: p.neighborhood || '',
                    complement: p.complement || '', city: p.city || '', state: p.state || '', zipCode: p.zipCode || '' };
      // Carregar billing e horários
      this.svc.getBilling(p.id).subscribe({ next: b => this.billing = { ...b }, error: () => {} });
      this.loadWorkingHours(p.id);
    } else {
      this.resetForms();
    }
    this.showModal = true;
  }

  openTab(tab: 'dados' | 'pagamento' | 'assinatura' | 'horarios' | 'usuarios' | 'indicacoes') {
    this.modalTab = tab;
    if (tab === 'assinatura' && this.selectedProfessional && !this.subscription) {
      this.svc.getSubscription(this.selectedProfessional.id).subscribe({ next: s => this.subscription = s, error: () => {} });
    }
    if (tab === 'usuarios' && this.selectedProfessional) {
      this.loadUsers();
      this.loadInvites();
      this.resetUserSearch();
    }
    if (tab === 'indicacoes' && this.selectedProfessional) {
      this.loadReferrals();
    }
  }

  // ── Usuários ────────────────────────────────────────────────────
  loadUsers(): void {
    this.api.getProfUsers(this.selectedProfessional.id).subscribe({
      next: u => this.users = u,
      error: () => {}
    });
  }

  resetUserSearch(): void {
    this.userSearchEmail = '';
    this.userSearchResult = null;
    this.userSearchError = '';
    this.userSearching = false;
    this.userLinkRole = 'Collaborator';
  }

  searchUser(): void {
    if (!this.userSearchEmail.trim()) return;
    this.userSearching = true;
    this.userSearchResult = null;
    this.userSearchError = '';
    this.api.searchUserByEmail(this.userSearchEmail.trim()).subscribe({
      next: u => { this.userSearchResult = u; this.userSearching = false; },
      error: () => { this.userSearchError = 'Usuário não encontrado'; this.userSearching = false; }
    });
  }

  linkUser(): void {
    if (!this.userSearchResult || !this.selectedProfessional) return;
    this.userLinking = true;
    this.api.addProfUser(this.selectedProfessional.id, this.userSearchResult.id, this.userLinkRole).subscribe({
      next: () => {
        this.userLinking = false;
        this.loadUsers();
        this.resetUserSearch();
      },
      error: (err: any) => {
        this.userLinking = false;
        this.notification.error('Erro ao Vincular', err?.error?.message || 'Não foi possível vincular o usuário ao profissional.');
      }
    });
  }

  removeUser(userId: number): void {
    this.notification.confirm(
      'Confirmar Remoção',
      'Deseja realmente remover este usuário do profissional?',
      () => {
        this.userRemovingId = userId;
        this.api.removeProfUser(this.selectedProfessional.id, userId).subscribe({
          next: () => {
            this.userRemovingId = null;
            this.loadUsers();
            this.notification.success('Usuário Removido', 'O usuário foi removido com sucesso.');
          },
          error: () => {
            this.userRemovingId = null;
            this.notification.error('Erro ao Remover', 'Não foi possível remover o usuário.');
          }
        });
      }
    );
  }

  // ── Convites ────────────────────────────────────────────────────
  sendInvite(): void {
    if (!this.inviteEmail.trim()) {
      this.notification.warning('Email Obrigatório', 'Por favor, digite um email válido.');
      return;
    }
    this.sendingInvite = true;
    this.api.createInvite(
      this.selectedProfessional.id,
      this.inviteEmail.trim(),
      this.inviteRole,
      this.invitePermissions
    ).subscribe({
      next: () => {
        this.sendingInvite = false;
        this.inviteEmail = '';
        this.loadInvites();
        this.notification.success('Convite Enviado', 'O convite foi enviado por email com sucesso!');
      },
      error: () => {
        this.sendingInvite = false;
        this.notification.error('Erro ao Enviar', 'Não foi possível enviar o convite. Tente novamente.');
      }
    });
  }

  loadInvites(): void {
    if (!this.selectedProfessional) return;
    this.api.getInvites(this.selectedProfessional.id).subscribe({
      next: invites => this.invites = invites,
      error: () => {}
    });
  }

  isExpired(date: string): boolean {
    return new Date(date) < new Date();
  }

  // ── Indicações ──────────────────────────────────────────────────
  loadReferrals(): void {
    if (!this.selectedProfessional) return;

    this.loadingReferrals = true;
    const filter: any = { pageSize: 100 };
    if (this.referralFilter.status) filter.status = this.referralFilter.status;

    this.api.getReferrals(this.selectedProfessional.id, filter).subscribe({
      next: result => {
        this.referrals = result.items;
        this.loadingReferrals = false;
      },
      error: () => this.loadingReferrals = false
    });
  }

  sendReferral(): void {
    if (!this.referralEmail?.trim()) {
      this.notification.warning('Campo obrigatório', 'Digite um email válido');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.referralEmail)) {
      this.notification.warning('Email inválido', 'Digite um endereço de email válido');
      return;
    }

    this.sendingReferral = true;
    this.api.createReferral(this.selectedProfessional.id, this.referralEmail).subscribe({
      next: () => {
        this.sendingReferral = false;
        this.referralEmail = '';
        this.loadReferrals();
        this.notification.success('Sucesso', 'Indicação enviada com sucesso!');
      },
      error: err => {
        this.sendingReferral = false;
        this.notification.error('Erro', err.error?.message || 'Erro ao enviar indicação');
      }
    });
  }

  deleteReferral(referral: any): void {
    if (!confirm(`Excluir indicação para ${referral.referredEmail}?`)) return;

    this.deletingReferralId = referral.id;
    this.api.deleteReferral(this.selectedProfessional.id, referral.id).subscribe({
      next: () => {
        this.deletingReferralId = null;
        this.loadReferrals();
      },
      error: err => {
        this.deletingReferralId = null;
        this.notification.error('Erro', err.error?.message || 'Erro ao excluir indicação');
      }
    });
  }

  // ── Horários ────────────────────────────────────────────────────
  private defaultWorkingHours(): any[] {
    return Array.from({ length: 7 }, (_, i) => ({
      dayOfWeek: i,
      slotDurationMinutes: 30,
      intervalMinutes: 0,
      morning:   { isActive: false, startTime: '08:00', endTime: '12:00' },
      afternoon: { isActive: false, startTime: '13:00', endTime: '18:00' }
    }));
  }

  loadWorkingHours(profId: number): void {
    this.workingHours = this.defaultWorkingHours();
    this.api.getWorkingHours(profId).subscribe({
      next: loaded => {
        // Mescla os registros carregados (um por bloco) no modelo de 2 blocos por dia
        loaded.forEach(wh => {
          const day = this.workingHours.find(d => d.dayOfWeek === wh.dayOfWeek);
          if (!day) return;
          // Detecta manhã por startTime < 12:00
          const isMorning = parseInt(wh.startTime.split(':')[0]) < 12;
          const block = isMorning ? 'morning' : 'afternoon';
          day[block] = { isActive: wh.isActive, startTime: wh.startTime, endTime: wh.endTime };
          day.slotDurationMinutes = wh.slotDurationMinutes;
          day.intervalMinutes     = wh.intervalMinutes;
        });
      },
      error: () => {}
    });
  }

  saveWorkingHours(): void {
    if (!this.selectedProfessional) return;
    this.savingHours = true;
    // Converte o modelo de 2 blocos por dia em array de WorkingHourRequest
    const toSave: any[] = [];
    this.workingHours.forEach(day => {
      if (day.morning.isActive) {
        toSave.push({
          dayOfWeek: day.dayOfWeek, startTime: day.morning.startTime, endTime: day.morning.endTime,
          slotDurationMinutes: day.slotDurationMinutes, intervalMinutes: day.intervalMinutes, isActive: true
        });
      }
      if (day.afternoon.isActive) {
        toSave.push({
          dayOfWeek: day.dayOfWeek, startTime: day.afternoon.startTime, endTime: day.afternoon.endTime,
          slotDurationMinutes: day.slotDurationMinutes, intervalMinutes: day.intervalMinutes, isActive: true
        });
      }
    });
    this.api.saveWorkingHours(this.selectedProfessional.id, toSave).subscribe({
      next: () => {
        this.savingHours = false;
        this.notification.success('Horários Salvos', 'Os horários de atendimento foram atualizados com sucesso!');
      },
      error: () => {
        this.savingHours = false;
        this.notification.error('Erro ao Salvar', 'Não foi possível salvar os horários de atendimento.');
      }
    });
  }

  close() { this.showModal = false; this.selectedProfessional = null; this.resetForms(); }

  get emailsMatchProf(): boolean {
    return !this.form.email || this.form.email === this.form.emailConfirm;
  }

  resetForms() {
    this.form = { name: '', specialty: '', professionalId: '', email: '', emailConfirm: '', phone: '', notes: '',
                  address: '', number: '', neighborhood: '', complement: '', city: '', state: '', zipCode: '' };
    this.billing = { preferredPaymentMethod: 1, pixKey: '42132309000185', pixKeyType: 2, pixHolderName: 'RA DIONISIO',
                     cardHolderName: '', cardLastFourDigits: '', cardBrand: '',
                     cardExpiryMonth: '', cardExpiryYear: '', cardGatewayToken: '', isConfigured: false };
    this.cardNumberDisplay = ''; this.cardExpiry = ''; this.cardCvv = '';
    this.workingHours = this.defaultWorkingHours();
  }

  save() {
    if (!this.form.name.trim()) {
      this.notification.warning('Campo Obrigatório', 'O nome do profissional é obrigatório.');
      return;
    }

    // Preparar billing
    if (this.billing.preferredPaymentMethod === 2 && this.cardNumberDisplay) {
      this.billing.cardLastFourDigits = this.cardNumberDisplay.replace(/\s/g, '').slice(-4);
      this.billing.isConfigured = true;
    }
    if (this.billing.preferredPaymentMethod === 1 && this.billing.pixKey) {
      this.billing.isConfigured = true;
    }

    this.loading = true;

    if (this.isEditing) {
      // Atualizar profissional
      this.svc.update(this.selectedProfessional.id, this.form).subscribe({
        next: () => {
          // Salvar billing separado
          this.svc.saveBilling(this.selectedProfessional.id, this.billing).subscribe({
            next: () => {
              this.load();
              this.close();
              this.loading = false;
              this.notification.success('Profissional Atualizado', 'Os dados do profissional foram atualizados com sucesso!');
            },
            error: () => { this.load(); this.close(); this.loading = false; }
          });
        },
        error: () => {
          this.loading = false;
          this.notification.error('Erro ao Atualizar', 'Não foi possível atualizar o profissional.');
        }
      });
    } else {
      // Criar novo
      this.svc.create(this.form).subscribe({
        next: (created: any) => {
          if (this.billing.isConfigured) {
            this.svc.saveBilling(created.id, this.billing).subscribe({ error: () => {} });
          }
          this.load();
          this.close();
          this.loading = false;
          this.notification.success(
            'Profissional Criado',
            'Profissional cadastrado com sucesso! Trial de 30 dias iniciado.\nR$ 39,90/mês após o período gratuito.'
          );
        },
        error: () => {
          this.loading = false;
          this.notification.error('Erro ao Cadastrar', 'Não foi possível cadastrar o profissional.');
        }
      });
    }
  }

  toggleStatus(p: any) {
    const acao = p.isActive ? 'desativar' : 'ativar';
    const acaoCapitalized = p.isActive ? 'Desativar' : 'Ativar';
    this.notification.confirm(
      `${acaoCapitalized} Profissional`,
      `Deseja realmente ${acao} o profissional ${p.name}?`,
      () => {
        this.svc.toggleStatus(p.id, !p.isActive).subscribe({
          next: () => {
            p.isActive = !p.isActive;
            this.notification.success(
              'Status Alterado',
              `O profissional foi ${p.isActive ? 'ativado' : 'desativado'} com sucesso.`
            );
          },
          error: () => this.notification.error('Erro ao Alterar Status', 'Não foi possível alterar o status do profissional.')
        });
      },
      undefined,
      'warning'
    );
  }

  // Formatação cartão
  formatCard(event: any) {
    let v = event.target.value.replace(/\D/g, '').substring(0, 16);
    this.cardNumberDisplay = v.replace(/(.{4})/g, '$1 ').trim();
    // Detectar bandeira
    if (v.startsWith('4')) this.billing.cardBrand = 'Visa';
    else if (v.startsWith('5')) this.billing.cardBrand = 'Mastercard';
    else if (v.startsWith('3')) this.billing.cardBrand = 'Amex';
    else this.billing.cardBrand = '';
  }

  formatExpiry(event: any) {
    let v = event.target.value.replace(/\D/g, '').substring(0, 4);
    if (v.length >= 2) v = v.substring(0, 2) + '/' + v.substring(2);
    this.cardExpiry = v;
    if (v.length === 5) {
      this.billing.cardExpiryMonth = v.substring(0, 2);
      this.billing.cardExpiryYear = '20' + v.substring(3);
    }
  }

  pixPlaceholder(): string {
    const m: any = { 1: '000.000.000-00', 2: '00.000.000/0001-00',
                     3: 'email@exemplo.com', 4: '(11) 98765-4321', 5: 'chave-aleatória' };
    return m[this.billing.pixKeyType] || '';
  }

  daysRemaining(date?: string): number {
    if (!date) return 0;
    return Math.max(0, Math.ceil((new Date(date).getTime() - Date.now()) / 86400000));
  }

  fmtDate(d?: string): string {
    return d ? new Date(d).toLocaleDateString('pt-BR') : '-';
  }

  fmtCurrency(v: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
  }

  subClass(status?: number): string {
    const m: any = { 1: 'bg-info', 2: 'bg-success', 3: 'bg-warning text-dark',
                     4: 'bg-danger', 5: 'bg-secondary', 6: 'bg-dark' };
    return `badge ${m[status!] || 'bg-secondary'}`;
  }

  subLabel(status?: number): string {
    const m: any = { 1: '🆓 Trial (30 dias)', 2: '✅ Ativo', 3: '⚠️ Pagamento Atrasado',
                     4: '🚫 Suspenso', 5: 'Cancelado', 6: 'Expirado' };
    return m[status!] || 'Sem assinatura';
  }

  payStatusLabel(s: number): string {
    return ['', 'Pendente', 'Pago', 'Falhou', 'Reembolsado', 'Cancelado'][s] || '-';
  }

  payMethodIcon(m: number): string {
    return m === 1 ? 'bi-qr-code' : 'bi-credit-card';
  }

  formatPhone(value: string): string {
    const v = value.replace(/\D/g, '').substring(0, 11);
    if (v.length === 0) return '';
    if (v.length <= 2)  return `(${v}`;
    if (v.length <= 6)  return `(${v.substring(0, 2)}) ${v.substring(2)}`;
    if (v.length <= 10) return `(${v.substring(0, 2)}) ${v.substring(2, 6)}-${v.substring(6)}`;
    return `(${v.substring(0, 2)}) ${v.substring(2, 7)}-${v.substring(7)}`;
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.formatPhone(input.value);
    this.form.phone = formatted;
    input.value = formatted;
  }
}
